import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getScenariosByTopic, getScenarioById } from '../data/scenarios';
import './ScenarioChallenge.css';

const ScenarioChallenge = () => {
  const { topic, scenarioId } = useParams();
  const navigate = useNavigate();
  const { awardXP, completeBoss } = useGame();

  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [scenarioComplete, setScenarioComplete] = useState(false);
  const [totalXPEarned, setTotalXPEarned] = useState(0);

  // Load scenarios
  useEffect(() => {
    if (scenarioId) {
      const scenario = getScenarioById(scenarioId);
      if (scenario) {
        setCurrentScenario(scenario);
        setCurrentStepIndex(0);
        setCompletedSteps([]);
        setScenarioComplete(false);
        setTotalXPEarned(0);
      }
    } else if (topic) {
      const topicScenarios = getScenariosByTopic(topic);
      setScenarios(topicScenarios);
    }
  }, [topic, scenarioId]);

  const currentStep = currentScenario?.steps[currentStepIndex];

  // Check if answer is correct
  const checkAnswer = () => {
    if (!currentStep) return;

    const cleanInput = userInput.trim();
    const isCorrect = currentStep.answer.some(ans =>
      cleanInput.toLowerCase() === ans.toLowerCase() ||
      cleanInput === ans
    );

    if (isCorrect) {
      const stepXP = Math.round(currentScenario.xpReward / currentScenario.steps.length);
      setTotalXPEarned(prev => prev + stepXP);

      setFeedback({
        correct: true,
        message: `Correct! +${stepXP} XP`,
        explanation: currentStep.explanation
      });

      setCompletedSteps(prev => [...prev, currentStepIndex]);

      // Move to next step after delay
      setTimeout(() => {
        if (currentStepIndex < currentScenario.steps.length - 1) {
          setCurrentStepIndex(prev => prev + 1);
          setUserInput('');
          setFeedback(null);
          setShowHint(false);
          setShowAnswer(false);
        } else {
          // Scenario complete!
          handleScenarioComplete();
        }
      }, 2000);
    } else {
      setFeedback({
        correct: false,
        message: "That's not quite right. Try again or check the hint."
      });
    }
  };

  const handleScenarioComplete = () => {
    setScenarioComplete(true);

    // Award XP based on difficulty
    const difficulty = currentScenario.difficulty || 'intermediate';
    awardXP(difficulty, {
      usedHint: showHint,
      usedAnswer: showAnswer,
      lessonId: currentScenario.id,
      topic: topic
    });

    // If it's a boss battle, mark it complete
    if (currentScenario.type === 'boss') {
      completeBoss();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkAnswer();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'detective': return '🕵️';
      case 'rescue': return '🚨';
      case 'boss': return '👑';
      case 'speedrun': return '⚡';
      default: return '🎯';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'detective': return '#9C27B0';
      case 'rescue': return '#F44336';
      case 'boss': return '#FF9800';
      case 'speedrun': return '#00BCD4';
      default: return '#4CAF50';
    }
  };

  // Scenario list view
  if (!currentScenario) {
    return (
      <div className="scenario-challenge">
        <div className="scenario-header">
          <Link to="/topics" className="back-link">← Back to Topics</Link>
          <h1>{topic?.charAt(0).toUpperCase() + topic?.slice(1)} Scenarios</h1>
          <p>Challenge yourself with real-world scenarios</p>
        </div>

        <div className="scenario-list">
          {scenarios.length === 0 ? (
            <div className="no-scenarios">
              <p>No scenarios available for this topic yet.</p>
              <Link to="/topics" className="btn">Choose Another Topic</Link>
            </div>
          ) : (
            scenarios.map(scenario => (
              <div
                key={scenario.id}
                className="scenario-card"
                style={{ borderLeftColor: getTypeColor(scenario.type) }}
                onClick={() => navigate(`/scenario/${topic}/${scenario.id}`)}
              >
                <div className="scenario-card-header">
                  <span className="scenario-type-icon">{getTypeIcon(scenario.type)}</span>
                  <span
                    className="scenario-type-badge"
                    style={{ backgroundColor: getTypeColor(scenario.type) }}
                  >
                    {scenario.type}
                  </span>
                </div>
                <h3>{scenario.title}</h3>
                <p className="scenario-narrative">{scenario.narrative.slice(0, 150)}...</p>
                <div className="scenario-meta">
                  <span className={`difficulty-badge difficulty-${scenario.difficulty}`}>
                    {scenario.difficulty}
                  </span>
                  <span className="scenario-xp">+{scenario.xpReward} XP</span>
                  <span className="scenario-steps">{scenario.steps.length} steps</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Scenario complete view
  if (scenarioComplete) {
    return (
      <div className="scenario-challenge scenario-complete-view">
        <div className="scenario-complete-container">
          <div className="complete-icon">{getTypeIcon(currentScenario.type)}</div>
          <h1>Mission Complete!</h1>
          <h2>{currentScenario.title}</h2>
          <div className="complete-stats">
            <div className="stat">
              <span className="stat-value">{completedSteps.length}</span>
              <span className="stat-label">Steps Completed</span>
            </div>
            <div className="stat">
              <span className="stat-value">+{totalXPEarned}</span>
              <span className="stat-label">XP Earned</span>
            </div>
          </div>
          <div className="complete-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/scenario/${topic}`)}
            >
              More Scenarios
            </button>
            <Link to="/topics" className="btn btn-secondary">
              Back to Topics
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Active scenario view
  return (
    <div className="scenario-challenge">
      <div className="scenario-header">
        <Link to={`/scenario/${topic}`} className="back-link">← Back to Scenarios</Link>
        <div className="scenario-title-row">
          <span className="scenario-type-icon">{getTypeIcon(currentScenario.type)}</span>
          <h1>{currentScenario.title}</h1>
          <span
            className="scenario-type-badge"
            style={{ backgroundColor: getTypeColor(currentScenario.type) }}
          >
            {currentScenario.type}
          </span>
        </div>
      </div>

      <div className="scenario-narrative-box">
        <p>{currentScenario.narrative}</p>
      </div>

      <div className="scenario-progress">
        <div className="progress-steps">
          {currentScenario.steps.map((_, idx) => (
            <div
              key={idx}
              className={`progress-step ${
                completedSteps.includes(idx) ? 'completed' :
                idx === currentStepIndex ? 'current' : ''
              }`}
            >
              {completedSteps.includes(idx) ? '✓' : idx + 1}
            </div>
          ))}
        </div>
        <div className="progress-text">
          Step {currentStepIndex + 1} of {currentScenario.steps.length}
        </div>
      </div>

      <div className="scenario-step">
        <div className="step-task">
          <h3>Your Mission:</h3>
          <p>{currentStep.task}</p>
        </div>

        <div className="terminal-container">
          <div className="terminal-header">
            <div className="terminal-buttons">
              <span className="terminal-btn red"></span>
              <span className="terminal-btn yellow"></span>
              <span className="terminal-btn green"></span>
            </div>
            <div className="terminal-title">Terminal - {currentScenario.type.toUpperCase()} MODE</div>
          </div>
          <div className="terminal">
            <div className="terminal-prompt">
              <span className="prompt-user">agent@mission</span>
              <span className="prompt-separator">:</span>
              <span className="prompt-directory">~</span>
              <span className="prompt-symbol">$</span>
            </div>

            <form onSubmit={handleSubmit} className="terminal-input-form">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className="terminal-input"
                placeholder="Enter your command..."
                autoFocus
              />
            </form>
          </div>
        </div>

        {feedback && (
          <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
            <p>{feedback.message}</p>
            {feedback.explanation && (
              <p className="feedback-explanation">{feedback.explanation}</p>
            )}
          </div>
        )}

        <div className="scenario-actions">
          <button
            onClick={() => setShowHint(true)}
            className="btn btn-secondary"
            disabled={showHint}
          >
            Hint
          </button>
          <button
            onClick={() => setShowAnswer(true)}
            className="btn btn-warning"
            disabled={showAnswer}
          >
            Show Answer
          </button>
          <button
            onClick={checkAnswer}
            className="btn btn-primary"
            disabled={!userInput}
          >
            Execute Command
          </button>
        </div>

        {showHint && (
          <div className="hint-box">
            <h4>Hint:</h4>
            <p>{currentStep.hint}</p>
          </div>
        )}

        {showAnswer && (
          <div className="answer-box">
            <h4>Answer:</h4>
            <code>{currentStep.answer[0]}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioChallenge;
